import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import api from '../lib/api';
import {
  Trash2, RotateCcw, RefreshCw, AlertTriangle, User, MapPin, DoorOpen, Hourglass, Eye,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ui/confirm-delete-dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../components/ui/sheet';

const TYPE_CONFIG = {
  employee: { label: 'Mitarbeiter', icon: User, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  location: { label: 'Standort', icon: MapPin, color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  room: { label: 'Raum', icon: DoorOpen, color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
};

export default function AdminTrash() {
  const [items, setItems] = useState([]);
  const [retentionDays, setRetentionDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [purging, setPurging] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = async (item) => {
    setDetailItem({ ...item, doc: null, impact: null });
    setDetailLoading(true);
    try {
      const [detailRes, impactRes] = await Promise.all([
        api.get(`/admin/trash/${item.resource_type}/${item.id}`),
        api.get(`/admin/trash/${item.resource_type}/${item.id}/impact`),
      ]);
      setDetailItem({ ...item, ...detailRes.data, impact: impactRes.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Details konnten nicht geladen werden');
      setDetailItem(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterType !== 'all' ? { resource_type: filterType } : {};
      const res = await api.get('/admin/trash', { params });
      setItems(res.data?.items || []);
      setRetentionDays(res.data?.retention_days || 30);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Papierkorb konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRestore = async (item) => {
    try {
      await api.post(`/admin/trash/${item.resource_type}/${item.id}/restore`);
      toast.success(`„${item.label}" wiederhergestellt`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wiederherstellung fehlgeschlagen');
    }
  };

  const handleHardDelete = async (item) => {
    try {
      await api.delete(`/admin/trash/${item.resource_type}/${item.id}`);
      toast.success(`„${item.label}" endgültig gelöscht`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Löschung fehlgeschlagen');
    }
  };

  const handlePurgeOld = async () => {
    setPurging(true);
    try {
      const res = await api.post('/admin/trash/purge', null, { params: { days: retentionDays } });
      toast.success(`${res.data?.deleted ?? 0} abgelaufene Einträge endgültig gelöscht`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sweep fehlgeschlagen');
    } finally {
      setPurging(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="admin-trash-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-xl sm:text-3xl text-foreground">Papierkorb</h1>
            <p className="text-muted-foreground mt-1">
              {items.length} Eintrag(e) — automatische Löschung nach {retentionDays} Tagen
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[200px]" data-testid="trash-filter-type">
                <SelectValue placeholder="Alle Typen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Typen</SelectItem>
                <SelectItem value="employee">Nur Mitarbeiter</SelectItem>
                <SelectItem value="location">Nur Standorte</SelectItem>
                <SelectItem value="room">Nur Räume</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadData} className="rounded-full" data-testid="trash-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Aktualisieren
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full text-destructive hover:text-destructive"
              onClick={handlePurgeOld}
              disabled={purging}
              data-testid="trash-purge-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Abgelaufene jetzt löschen
            </Button>
          </div>
        </div>

        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-900 dark:text-amber-100">DSGVO-Hinweis</p>
              <p className="text-amber-800 dark:text-amber-200/80 mt-1">
                Einträge älter als {retentionDays} Tage werden täglich um 02:00 Uhr automatisch endgültig gelöscht.
                Manuelle Endgültig-Löschung („Recht auf Löschung") ist jederzeit möglich.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1,2,3].map((i) => <Skeleton key={`s-${i}`} className="h-12 w-full" />)}
              </div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground" data-testid="trash-empty">
                <Trash2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Der Papierkorb ist leer</p>
                <p className="text-sm mt-1">Hier landen gelöschte Mitarbeiter, Standorte und Räume.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Typ</TableHead>
                    <TableHead>Eintrag</TableHead>
                    <TableHead className="w-[180px]">Gelöscht von</TableHead>
                    <TableHead className="w-[180px]">Gelöscht am</TableHead>
                    <TableHead className="w-[120px]">Verbleibend</TableHead>
                    <TableHead className="w-[380px] text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const cfg = TYPE_CONFIG[item.resource_type] || TYPE_CONFIG.employee;
                    const Icon = cfg.icon;
                    const expired = item.days_remaining <= 0;
                    return (
                      <TableRow key={`${item.resource_type}-${item.id}`} data-testid={`trash-row-${item.id}`}>
                        <TableCell className="py-3">
                          <Badge variant="outline" className={`text-xs gap-1 ${cfg.color}`}>
                            <Icon className="h-3 w-3" />
                            {cfg.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-3">
                          <p className="text-sm font-medium">{item.label}</p>
                          {item.subtitle && (
                            <p className="text-[11px] text-muted-foreground">{item.subtitle}</p>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-sm">{item.deleted_by || '—'}</TableCell>
                        <TableCell className="py-3 text-sm">
                          {item.deleted_at ? format(parseISO(item.deleted_at), 'dd.MM.yyyy HH:mm', { locale: de }) : '—'}
                        </TableCell>
                        <TableCell className="py-3">
                          <Badge variant="outline" className={`gap-1 ${expired ? 'bg-red-100 text-red-800' : 'bg-muted'}`}>
                            <Hourglass className="h-3 w-3" />
                            {expired ? 'Abgelaufen' : `${item.days_remaining} Tage`}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-3 text-right space-x-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-full"
                            onClick={() => openDetail(item)}
                            data-testid={`trash-detail-${item.id}`}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            Details
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            onClick={() => handleRestore(item)}
                            data-testid={`trash-restore-${item.id}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5 mr-1" />
                            Wiederherstellen
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full text-destructive hover:text-destructive"
                            onClick={() => setHardDeleteTarget(item)}
                            data-testid={`trash-harddelete-${item.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Endgültig löschen
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <ConfirmDeleteDialog
          open={!!hardDeleteTarget}
          onOpenChange={(o) => !o && setHardDeleteTarget(null)}
          title="Endgültig löschen?"
          description={
            hardDeleteTarget
              ? `Möchten Sie „${hardDeleteTarget.label}" UNWIDERRUFLICH löschen? Diese Aktion kann nicht rückgängig gemacht werden — eine Wiederherstellung ist danach NICHT mehr möglich. Vorgang wird in den Zugriffsprotokollen vermerkt.`
              : ''
          }
          confirmLabel="Endgültig löschen"
          onConfirm={() => {
            if (hardDeleteTarget) handleHardDelete(hardDeleteTarget);
            setHardDeleteTarget(null);
          }}
          testIdPrefix="confirm-harddelete"
        />

        <Sheet open={!!detailItem} onOpenChange={(o) => !o && setDetailItem(null)}>
          <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto" data-testid="trash-detail-sheet">
            <SheetHeader>
              <SheetTitle>{detailItem?.label || 'Details'}</SheetTitle>
              <SheetDescription>
                Vorschau des soft-gelöschten Eintrags vor Wiederherstellung oder endgültiger Löschung.
              </SheetDescription>
            </SheetHeader>

            {detailLoading || !detailItem?.doc ? (
              <div className="space-y-3 mt-6">
                {[1,2,3,4,5].map((i) => <Skeleton key={`d-${i}`} className="h-8 w-full" />)}
              </div>
            ) : (
              <div className="space-y-6 mt-6 text-sm">
                {/* Lösch-Metadaten */}
                <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
                  <CardContent className="p-3 space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Gelöscht am</span>
                      <span className="font-medium">
                        {detailItem.doc.deleted_at
                          ? format(parseISO(detailItem.doc.deleted_at), 'dd.MM.yyyy HH:mm', { locale: de })
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Gelöscht von</span>
                      <span className="font-medium">
                        {detailItem.doc.deleted_by_name || detailItem.doc.deleted_by || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Verbleibend</span>
                      <span className="font-medium">
                        {detailItem.days_remaining > 0
                          ? `${detailItem.days_remaining} Tage`
                          : 'Abgelaufen'}
                      </span>
                    </div>
                    {detailItem.doc.deletion_reason && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Grund</span>
                        <span className="font-medium">{detailItem.doc.deletion_reason}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Folge-Auswirkungen */}
                {detailItem.impact && (
                  (detailItem.impact.restore_effects?.length > 0 ||
                   detailItem.impact.hard_delete_warnings?.length > 0) ? (
                    <div className="space-y-2">
                      <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        Folge-Auswirkungen
                      </p>
                      {detailItem.impact.restore_effects?.length > 0 && (
                        <Card className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800">
                          <CardContent className="p-3">
                            <p className="text-xs font-medium text-emerald-900 dark:text-emerald-100 flex items-center gap-1.5">
                              <RotateCcw className="h-3.5 w-3.5" />
                              Bei Wiederherstellung
                            </p>
                            <ul className="mt-2 space-y-1 text-xs text-emerald-900 dark:text-emerald-100/90">
                              {detailItem.impact.restore_effects.map((e, i) => (
                                <li key={`r-${i}`} className="flex gap-1.5"><span>•</span><span>{e}</span></li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      )}
                      {detailItem.impact.hard_delete_warnings?.length > 0 && (
                        <Card className="border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800">
                          <CardContent className="p-3">
                            <p className="text-xs font-medium text-red-900 dark:text-red-100 flex items-center gap-1.5">
                              <AlertTriangle className="h-3.5 w-3.5" />
                              Bei endgültiger Löschung
                            </p>
                            <ul className="mt-2 space-y-1 text-xs text-red-900 dark:text-red-100/90">
                              {detailItem.impact.hard_delete_warnings.map((w, i) => (
                                <li key={`w-${i}`} className="flex gap-1.5"><span>•</span><span>{w}</span></li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  ) : null
                )}

                {/* Original-Felder */}
                <div>
                  <p className="font-medium mb-2 text-muted-foreground text-xs uppercase tracking-wide">
                    Original-Daten
                  </p>
                  <div className="border rounded-lg divide-y">
                    {Object.entries(detailItem.doc)
                      .filter(([k]) => !['deleted_at', 'deleted_by', 'deleted_by_name', 'deletion_reason', 'original_collection'].includes(k))
                      .map(([key, value]) => (
                        <div key={key} className="flex justify-between gap-3 py-2 px-3">
                          <span className="text-muted-foreground text-xs flex-shrink-0">{key}</span>
                          <span className="text-xs text-right break-all max-w-[60%]">
                            {value === null || value === undefined || value === ''
                              ? <span className="text-muted-foreground italic">leer</span>
                              : typeof value === 'object'
                                ? <code className="text-[10px] bg-muted px-1 rounded">{JSON.stringify(value)}</code>
                                : String(value)}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    className="flex-1 rounded-full"
                    onClick={() => { handleRestore(detailItem); setDetailItem(null); }}
                    data-testid="detail-restore-btn"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Wiederherstellen
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 rounded-full text-destructive hover:text-destructive"
                    onClick={() => { setHardDeleteTarget(detailItem); setDetailItem(null); }}
                    data-testid="detail-harddelete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Endgültig löschen
                  </Button>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
